"use client";

import Link from "next/link";
import Image from "next/image";
import {useEffect, useState, useCallback, useRef} from "react";
import {usePathname, useRouter} from "next/navigation";
import {supabase} from "@/lib/supabase";
import type {User} from "@/types";
import UserProfileMenu from "./UserProfileMenu";
import NotificationBell from "./NotificationBell";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8080";

export default function Header() {
  const [user, setUser] = useState<User | null>(null);
  const isCheckingRef = useRef(false);
  const router = useRouter();
  const pathname = usePathname();

  const checkUser = useCallback(async () => {
    if (isCheckingRef.current) return; // Prevent concurrent checks
    isCheckingRef.current = true;

    // In test mode, skip Supabase auth (we use test auth instead)
    const isTestMode = process.env.NEXT_PUBLIC_TEST_MODE === "true";
    if (isTestMode) {
      try {
        const response = await fetch(`${API_URL}/api/me`, {
          credentials: "include",
        });
        if (response.ok) {
          const testUser = (await response.json()) as User;
          setUser(testUser);
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error("Failed to load test session user:", error);
        setUser(null);
      }
      isCheckingRef.current = false;
      return;
    }

    try {
      const {
        data: {session},
      } = await supabase.auth.getSession();
      if (session) {
        // Call backend to get user profile
        const token = session.access_token;
        localStorage.setItem("auth_token", token);

        // Try to get user from backend
        try {
          const response = await fetch(
            `${
              process.env.NEXT_PUBLIC_API_URL || "http://localhost:8080"
            }/api/me`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
          } else if (response.status === 403) {
            const payload = await response.json().catch(() => null);

            if (payload?.code === "EARLY_ACCESS_REQUIRED") {
              window.localStorage.setItem(
                "early_access_payload",
                JSON.stringify(payload)
              );
              if (pathname !== "/early-access") {
                router.push("/early-access");
              }
            }

            const tempUser: User = {
              id: session.user.id,
              email: session.user.email || "",
              name:
                session.user.user_metadata?.full_name ||
                session.user.email?.split("@")[0] ||
                "User",
              role: "customer",
              auth_provider: (session.user.app_metadata?.provider ||
                "email") as "google" | "apple" | "email",
              is_vendor_approved: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            };
            setUser(tempUser);
          } else {
            // If backend doesn't have user yet, create a temporary user from Supabase session
            // This allows the UI to show the user is logged in while backend sync happens
            const tempUser: User = {
              id: session.user.id,
              email: session.user.email || "",
              name:
                session.user.user_metadata?.full_name ||
                session.user.email?.split("@")[0] ||
                "User",
              role: "customer", // Default role until backend syncs
              auth_provider: (session.user.app_metadata?.provider ||
                "email") as "google" | "apple" | "email",
              is_vendor_approved: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            };
            setUser(tempUser);
            console.log(
              "Backend user not found, using Supabase session. User may need to be synced to backend."
            );
          }
        } catch (apiError) {
          // If API call fails, use Supabase session as fallback
          console.error("Error fetching user from API:", apiError);
          const tempUser: User = {
            id: session.user.id,
            email: session.user.email || "",
            name:
              session.user.user_metadata?.full_name ||
              session.user.email?.split("@")[0] ||
              "User",
            role: "customer",
            auth_provider: (session.user.app_metadata?.provider || "email") as
              | "google"
              | "apple"
              | "email",
            is_vendor_approved: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };
          setUser(tempUser);
        }
      } else {
        setUser(null);
        localStorage.removeItem("auth_token");
      }
    } catch (error) {
      console.error("Error checking user:", error);
    } finally {
      isCheckingRef.current = false;
    }
  }, [pathname, router]);

  useEffect(() => {
    let mounted = true;

    checkUser();

    const {
      data: {subscription},
    } = supabase.auth.onAuthStateChange(() => {
      if (mounted) {
        checkUser();
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [checkUser]);

  const handleSignOut = async () => {
    // In test mode, clear test session cookie
    const isTestMode = process.env.NEXT_PUBLIC_TEST_MODE === "true";
    if (isTestMode) {
      // Clear test session cookie by setting it to expire
      document.cookie =
        "test_session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    } else {
      // Sign out from Supabase
      await supabase.auth.signOut();
    }

    // Clear user state and auth token
    setUser(null);
    localStorage.removeItem("auth_token");

    // Redirect to home page
    router.push("/");
  };

  // Normalize role to prevent case sensitivity issues
  const normalizedRole = user?.role?.toLowerCase() || null;
  const isVendor = normalizedRole === "vendor";
  const isAdmin = normalizedRole === "admin";
  const showVendorLink = isVendor || isAdmin;
  const vendorLinkHref = isAdmin ? "/admin" : "/dashboard";
  const vendorLinkLabel = isAdmin ? "Admin Dashboard" : "Vendor Dashboard";

  return (
    <header
      className="sticky top-0 z-50 border-b-2"
      style={{backgroundColor: "#0A3740", borderColor: "#D4AF37"}}
    >
      <div className="max-w-6xl mx-auto px-3 sm:px-4 lg:px-6">
        <div className="flex items-stretch justify-between gap-4 py-0">
          <Link
            href="/"
            className="flex items-center hover:opacity-90 transition-opacity py-2"
          >
            <Image
              src="https://pacificnorthnorth-images-noprod.s3.us-east-1.amazonaws.com/assets/PacificNorthNorthHeader.svg"
              alt="Pacific North North"
              width={420}
              height={60}
              className="h-10 md:h-12 w-auto"
              priority
              fetchPriority="high"
            />
          </Link>
          <nav className="flex items-center gap-6 text-sm font-semibold uppercase tracking-[0.08em]">
            <Link
              href="/listings"
              className="transition-colors hover:text-[#E8C85C]"
              style={{color: "#D4AF37"}}
            >
              Explore
            </Link>
            {showVendorLink && (
              <Link
                href={vendorLinkHref}
                className="transition-colors hover:text-[#E8C85C]"
                style={{color: "#D4AF37"}}
              >
                {vendorLinkLabel}
              </Link>
            )}
            {user ? (
              <>
                <div className="hidden sm:flex items-center gap-3">
                  <NotificationBell user={user} />
                  <UserProfileMenu user={user} />
                </div>
                {/* Mobile: simple sign out button */}
                <button
                  onClick={handleSignOut}
                  className="sm:hidden transition-colors hover:text-[#E8C85C] text-xs"
                  style={{color: "#D4AF37"}}
                >
                  Sign Out
                </button>
              </>
            ) : (
              <div className="flex items-center gap-4">
                <Link href="/auth" className="btn-primary normal-case">
                  Sign In
                </Link>
              </div>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
}
